/**
 * docxSections.ts
 * Word sections are the unit of "one document frame": each section break
 * (Next Page) produces one physical page in the render pipeline, so a template
 * holding several distinct forms holds them as several sections.
 *
 * In WordprocessingML the properties of a section live in a `<w:sectPr>` that
 * sits inside the `<w:pPr>` of the section's *last* paragraph; the final
 * section of the document instead keeps its `<w:sectPr>` as a direct child of
 * `<w:body>`. Both shapes are handled here.
 */

import PizZip from "pizzip";

/** Matches a `{{fieldName}}` placeholder (excludes {{#loop}}/{{/loop}} controls). */
const PLACEHOLDER_RE = /\{\{(?!#|\/)[^{}]*\}\}/g;

export interface BodySplit {
  prefix: string;
  body: string;
  suffix: string;
}

/** Split document.xml into everything before, inside and after `<w:body>`. */
export function extractBody(docXml: string): BodySplit | null {
  const openIdx = docXml.indexOf("<w:body>");
  const closeIdx = docXml.lastIndexOf("</w:body>");
  if (openIdx === -1 || closeIdx === -1 || closeIdx < openIdx) return null;
  const bodyStart = openIdx + "<w:body>".length;
  return {
    prefix: docXml.slice(0, bodyStart),
    body: docXml.slice(bodyStart, closeIdx),
    suffix: docXml.slice(closeIdx),
  };
}

export interface SectionRange {
  index: number;
  /** Offsets into the body string. */
  start: number;
  end: number;
  /** The `<w:sectPr>…</w:sectPr>` XML governing this section, if present. */
  sectPrXml: string;
  /** True when the sectPr lives inside the last paragraph rather than the body. */
  inline: boolean;
  /** Offsets of the sectPr element itself, within the body string. */
  sectPrStart: number;
  sectPrEnd: number;
}

function findSectPrElements(body: string): { start: number; end: number; xml: string }[] {
  const found: { start: number; end: number; xml: string }[] = [];
  const re = /<w:sectPr\b[^>]*(?:\/>|>[\s\S]*?<\/w:sectPr>)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(body)) !== null) {
    found.push({ start: m.index, end: m.index + m[0].length, xml: m[0] });
  }
  return found;
}

/**
 * The `<w:p>` containing an offset, or null when the offset sits at body level.
 * Scans backwards past sibling tags that merely start with `<w:p` (`<w:pPr>`,
 * `<w:pStyle>`) until it finds the paragraph element itself.
 */
function enclosingParagraph(body: string, offset: number): { start: number; end: number } | null {
  let from = offset;
  while (from >= 0) {
    const start = body.lastIndexOf("<w:p", from);
    if (start === -1) return null;
    if (/^<w:p[ >/]/.test(body.slice(start, start + 6))) {
      const end = body.indexOf("</w:p>", offset);
      if (end === -1) return null;
      return { start, end: end + "</w:p>".length };
    }
    from = start - 1;
  }
  return null;
}

/**
 * Every section of the document, in order. A document without any section
 * break still yields one section covering the whole body.
 */
export function findSectionRanges(body: string): SectionRange[] {
  const sectPrs = findSectPrElements(body);
  const ranges: SectionRange[] = [];
  let cursor = 0;

  sectPrs.forEach((sectPr, index) => {
    const para = enclosingParagraph(body, sectPr.start);
    const inline = para !== null && para.start < sectPr.start && para.end > sectPr.end;
    const end = inline ? para!.end : body.length;
    ranges.push({
      index,
      start: cursor,
      end,
      sectPrXml: sectPr.xml,
      inline,
      sectPrStart: sectPr.start,
      sectPrEnd: sectPr.end,
    });
    cursor = end;
  });

  if (ranges.length === 0) {
    ranges.push({
      index: 0,
      start: 0,
      end: body.length,
      sectPrXml: "",
      inline: false,
      sectPrStart: -1,
      sectPrEnd: -1,
    });
  }

  return ranges;
}

export interface SectionSummary {
  index: number;
  /** Number of ⟦FIELD⟧ markers inside the section. */
  fieldCount: number;
  /** Field labels detected in the section, when the caller provides them. */
  fieldNames: string[];
  /** True when the section holds no marker at all (cover page or mistake). */
  empty: boolean;
}

export interface SectionAnalysis {
  sections: SectionSummary[];
  warnings: string[];
}

/**
 * Summarize the sections of a template: how many frames it holds and which
 * fields belong to each. Markers are located by their offset in document.xml,
 * so the caller passes the offsets returned by `extractDocxMarkers`.
 */
export function analyzeSections(
  docXml: string,
  fields: { fieldName: string; markerOffset: number }[] = []
): SectionAnalysis {
  const split = extractBody(docXml);
  const warnings: string[] = [];
  if (!split) {
    return { sections: [], warnings: ["Không đọc được phần thân (<w:body>) của template."] };
  }

  const ranges = findSectionRanges(split.body);
  const bodyOffset = split.prefix.length;

  const sections = ranges.map((range) => {
    const inSection = fields.filter((f) => {
      const rel = f.markerOffset - bodyOffset;
      return rel >= range.start && rel < range.end;
    });
    const markerCount = (
      split.body.slice(range.start, range.end).match(PLACEHOLDER_RE) ?? []
    ).length;
    return {
      index: range.index,
      fieldCount: markerCount,
      fieldNames: Array.from(new Set(inSection.map((f) => f.fieldName))),
      empty: markerCount === 0,
    };
  });

  sections.forEach((section) => {
    if (section.empty) {
      warnings.push(
        `Khung ${section.index + 1} không chứa placeholder {{tên}} — có thể là trang bìa hoặc lỗi template.`
      );
    }
  });

  if (usesManualPageBreaks(split.body) && ranges.length === 1) {
    warnings.push(
      "Template dùng ngắt trang thủ công thay vì Section Break (Next Page) — các chứng từ phía sau có thể bị gộp hoặc cắt khi xuất."
    );
  }

  return { sections, warnings };
}

/** Convenience wrapper: analyze the sections of a .docx package. */
export function analyzeDocxSections(
  fileBuffer: ArrayBuffer | Uint8Array,
  fields: { fieldName: string; markerOffset: number }[] = []
): SectionAnalysis {
  const zip = new PizZip(fileBuffer);
  const docXml = zip.files["word/document.xml"]?.asText() ?? "";
  return analyzeSections(docXml, fields);
}

/** True when the body contains a manual page break (`<w:br w:type="page"/>`). */
export function usesManualPageBreaks(body: string): boolean {
  return /<w:br\b[^>]*w:type="page"/.test(body);
}

/**
 * Cut a multi-section template into one standalone DOCX per section.
 * Everything else in the package (styles, headers/footers, media, relations)
 * is copied untouched; only `word/document.xml` differs between the outputs.
 */
export function splitDocxBySection(fileBuffer: ArrayBuffer | Uint8Array): Uint8Array[] {
  const zip = new PizZip(fileBuffer);
  const docXml = zip.files["word/document.xml"]?.asText() ?? "";
  const split = extractBody(docXml);
  if (!split) throw new Error("Không đọc được phần thân (<w:body>) của template.");

  const ranges = findSectionRanges(split.body);
  if (ranges.length <= 1) return [new Uint8Array(toUint8(fileBuffer))];

  return ranges.map((range) => {
    let bodyXml = split.body.slice(range.start, range.end);

    // Move an inline sectPr up to body level so each output is a well-formed
    // single-section document instead of one whose last paragraph carries it.
    // A body-level sectPr already sits at the end of the slice.
    if (range.inline && range.sectPrXml) {
      bodyXml = stripEmptyParagraphProps(bodyXml.split(range.sectPrXml).join(""));
      bodyXml += range.sectPrXml;
    }

    const partZip = new PizZip(fileBuffer);
    partZip.file("word/document.xml", split.prefix + bodyXml + split.suffix);
    return partZip.generate({
      type: "uint8array",
      mimeType:
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    });
  });
}

/** Remove `<w:pPr></w:pPr>` left behind after lifting a sectPr out of it. */
function stripEmptyParagraphProps(xml: string): string {
  return xml.replace(/<w:pPr>\s*<\/w:pPr>/g, "");
}

/**
 * Duplicate one Word section (page) of the template and append the copy right
 * after it, as its own new section. Lets the user add an extra copy of a form
 * (e.g. a second delivery note) and then edit it in the template canvas.
 *
 * The copy reuses the target section's page properties (`<w:sectPr>`), so the
 * duplicated page keeps the same layout. The final body-level `<w:sectPr>` (if
 * any) stays the last body child.
 */
export function duplicateSectionInDocx(
  fileBuffer: ArrayBuffer | Uint8Array,
  sectionIndex: number
): Uint8Array {
  const zip = new PizZip(fileBuffer);
  const docXml = zip.files["word/document.xml"]?.asText() ?? "";
  const split = extractBody(docXml);
  if (!split) throw new Error("Không đọc được phần thân (<w:body>) của template.");
  const ranges = findSectionRanges(split.body);
  if (ranges.length === 0) throw new Error("Không tìm thấy section nào trong template.");

  const si = Math.max(0, Math.min(sectionIndex, ranges.length - 1));
    const range = ranges[si];
    const sectPrXml = range.sectPrXml || "<w:sectPr/>";
    const breakPara = `<w:p><w:pPr>${sectPrXml}</w:pPr></w:p>`;

    // Layout of the result (N = sectionIndex):
    //   … | section N content | break(sectPr) | COPY content | …rest…
    // For the final body-level section the original trailing <w:sectPr> becomes
    // the properties of the NEW last section (the copy).
    //
    // Do NOT insert `content + break` before a body-level sectPr — that stacks
    // both copies inside section N and leaves the new section empty (the bug
    // users saw as "copy vào page đang chọn, chưa ra page mới").
    let newBody: string;

    if (range.inline) {
      // Middle section: content ends before the break paragraph; insert the copy
      // (content + its own break) immediately after the original break.
      const para = enclosingParagraph(split.body, range.sectPrStart);
      const contentEnd = para ? para.start : range.sectPrStart;
      const content = split.body.slice(range.start, contentEnd);
      const insertAt = range.end;
      newBody =
        split.body.slice(0, insertAt) + content + breakPara + split.body.slice(insertAt);
    } else {
      // Final (body-level) section — including single-section templates.
      //   [orig content][breakPara][copy content][body-level sectPr]
      const contentEnd =
        range.sectPrStart >= 0 ? range.sectPrStart : split.body.length;
      const content = split.body.slice(range.start, contentEnd);
      const trailing =
        range.sectPrStart >= 0 ? split.body.slice(contentEnd) : sectPrXml;
      newBody =
        split.body.slice(0, range.start) + content + breakPara + content + trailing;
    }

    zip.file("word/document.xml", split.prefix + newBody + split.suffix);
  return zip.generate({
    type: "uint8array",
    mimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  });
}

function toUint8(buffer: ArrayBuffer | Uint8Array): Uint8Array {
  return buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
}

/** Strip any `<w:sectPr>` from a paragraph, leaving a normal (possibly empty) paragraph. */
function stripSectPrFromParagraph(paraXml: string): string {
  return paraXml
    .replace(/<w:sectPr\b[^>]*>[\s\S]*?<\/w:sectPr>/g, "")
    .replace(/<w:sectPr\b[^>]*\/>/g, "")
    .replace(/<w:pPr>\s*<\/w:pPr>/g, "");
}

/**
 * Remove one Word section (page) from the template. When the last (body-level)
 * section is removed, the previous section's properties are lifted to body
 * level so the document stays well-formed and does not leave a blank page.
 */
export function deleteSectionInDocx(
  fileBuffer: ArrayBuffer | Uint8Array,
  sectionIndex: number
): Uint8Array {
  const zip = new PizZip(fileBuffer);
  const docXml = zip.files["word/document.xml"]?.asText() ?? "";
  const split = extractBody(docXml);
  if (!split) throw new Error("Không đọc được phần thân (<w:body>) của template.");
  const ranges = findSectionRanges(split.body);
  if (ranges.length <= 1) throw new Error("Không thể xóa section duy nhất còn lại.");

  const si = Math.max(0, Math.min(sectionIndex, ranges.length - 1));
  const range = ranges[si];
  let newBody: string;

  if (range.inline) {
    // Remove the section content plus its terminating section-break paragraph.
    const para = enclosingParagraph(split.body, range.sectPrStart);
    const end = para ? para.end : range.end;
    newBody = split.body.slice(0, range.start) + split.body.slice(end);
  } else {
    // Last section (body-level sectPr): drop its content, keep the previous
    // section's content and lift that section's properties to body level.
    const prev = ranges[si - 1];
    const prevSectPrXml = prev.sectPrXml || "<w:sectPr/>";
    let prefix = split.body.slice(0, range.start);
    if (prev.inline) {
      const prevPara = enclosingParagraph(split.body, prev.sectPrStart);
      if (prevPara) {
        const stripped = stripSectPrFromParagraph(
          prefix.slice(prevPara.start, prevPara.end)
        );
        prefix =
          prefix.slice(0, prevPara.start) + stripped + prefix.slice(prevPara.end);
      }
    }
    newBody = prefix + prevSectPrXml;
  }

  zip.file("word/document.xml", split.prefix + newBody + split.suffix);
  return zip.generate({
    type: "uint8array",
    mimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  });
}
